import React, { useState, useRef, useEffect, useMemo } from "react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import GameService from "../../../service/gameService";
import Auth from "../../../service/Auth";
import TransferService from "../../../service/Transfer";
import { useAuth } from "../../../providers/AuthContext";
import { FETCH_DATE_AFTER_SELECTION } from "../../../helpers/functions";
import { API_URL } from "../../../helpers/constants";
import SelectDropDown from "../../../components/ui/SelectDropDown";
import { SectionLoader } from "../../../components/ui/loaders/SectionLoader";
import Modal from "../../../components/ui/Modal";
import CustomTable from "../../../components/ui/tables/CustomTable";
import SelectedDates from "../FilterFormComponents/DateFilter";
import RoleBasedDropdowns, { roleQueryParams } from "../FilterFormComponents/RoleBasedDropdown";
import CasinoBetDetailModal from "./CasinoBetDetailModal";
import { ReactComponent as IconSearch } from "../../../assets/icons/icon-search.svg";
import { FaInfoCircle } from "react-icons/fa";
// import CasinoBetModal from "../User/Betting/casinoBetModal";

const CasinoBets = () => {
  const { t } = useTranslation();
  const selectRef = useRef(null);

  const authService = new Auth(API_URL);
  const transferServ = new TransferService(API_URL);
  const gameService = new GameService(API_URL);
  const [selectedDate, setSelectedDate] = useState("today");
  const [showReportData, setShowReportData] = useState(false);

  const { user } = useAuth();
  const [chosenDate, setChosenDate] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [message, setMessage] = useState("");
  const [selectedGameOptions, setSelectedGameOptions] = useState(null);
  const [date, setDate] = useState({ startDate: "", endDate: "" });
  const [casinoBetsResponse, setCasinoBetsResponse] = useState(null);
  const [casinoBetsLoading, setCasinoBetsLoading] = useState(false);
  const [betId, setBetId] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRowDetailModalOpen, setIsRowDetailModalOpen] = useState(false);
  const [selectedRowDetail, setSelectedRowDetail] = useState(null);

  const [selectedRole, setSelectedRole] = useState({});
  const [selectedInputValues, setSelectedInputValues] = useState({});

  useEffect(() => {
    const { startDate, endDate } = FETCH_DATE_AFTER_SELECTION(selectedDate, chosenDate);
    setDate({ ...date, startDate: startDate, endDate: endDate });
  }, [selectedDate, chosenDate]);

  const fetchGames = async (inputValue, callback) => {
    if (inputValue.length < 3) {
      callback([]);
      return;
    }
    try {
      let options = [];

      try {
        const response = await gameService.searchAllGames(inputValue);
        options = response.data
          .filter((fi) => fi.name !== user.name && fi.name.toLowerCase().includes(inputValue.toLowerCase()))
          .map((item) => ({
            value: item.gameId,
            label: item.name,
          }));
        // setGames(response.data);
        // setTotalGames(response?.pagination?.total);
      } catch (error) {
        console.error("Failed to load games:", error);
      } finally {
        setLoading(false);
      }
      callback(options);
    } catch (error) {
      console.error("Error fetching users:", error);
      callback([]);

      // Handle token expiration or invalid token (401)
      if (error.response?.status === 401) {
        setMessage("Authorization failed. Please log in again.");
      } else {
        setMessage("Error fetching users. Please try again later.");
      }
    }
  };

  const handleGameChange = (option) => {
    setSelectedGameOptions(option);
    if (selectRef.current) {
      selectRef.current.blur();
    }
  };

  const handelSearchData = async () => {
    setMessage(null);
    try {
      setCasinoBetsLoading(true);
      const userIds = Object.entries(selectedInputValues)
        .map(([role, value]) => (value ? `&${roleQueryParams[role]}=${value}` : ""))
        .join("");
      let query = `${userIds}${selectedGameOptions?.value ? `&gameId=${selectedGameOptions?.value}` : ""}${betId ? `&betId=${betId}` : ""}`;
      const casinoBetresponse = await transferServ.getAllCasinoBets({
        startDate: date.startDate,
        endDate: date.endDate,
        query: query,
      });
      if (casinoBetresponse.success && casinoBetresponse?.bets?.length) {
        setCasinoBetsResponse([...casinoBetresponse?.bets]);
      } else {
        setCasinoBetsResponse([]);
        console.error("Unexpected response structure:", casinoBetresponse);
        setMessage("No Reports found.");
      }
      setCasinoBetsLoading(false);
    } catch (error) {
      setCasinoBetsLoading(false);
      console.error("Error fetching users:", error);
      // Handle token expiration or invalid token (401)
      if (error.response?.status === 401) {
        setMessage("Authorization failed. Please log in again.");
        // Optional: Redirect to login page
        // navigate("/login");
      } else {
        setMessage("Error fetching users. Please try again later.");
      }
    } finally {
      setCasinoBetsLoading(false);
    }
  };

  const tableData = casinoBetsResponse?.map((item, index) => {
    return {
      ...item,
    };
  });

  const tableColumns = useMemo(
    () => [
      {
        accessorKey: "sender.username",
        header: "Username",
        size: 100,
      },
      {
        accessorKey: "gameName",
        header: "Game",
        size: 200,
        Cell: ({ cell }) =>
          cell.row.original?.gameName ? (
            cell.row.original?.gameName
          ) : (
            <div class="bg-slate-100 max-w-20 text-center text-gray-700 rounded relative" role="alert">
              <strong class="font-bold">-</strong>
            </div>
          ),
      },
      {
        accessorKey: "date",
        header: "Bet date",
        size: 150,
        Cell: ({ cell }) => moment?.tz(cell.row.original?.date, "UTC")?.format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        accessorKey: "stake",
        header: "Stake",
        size: 50,
        Cell: ({ cell }) => (
          <div className="flex">
            <>
              {cell.row.original?.stake} {cell.row.original.currency}{" "}
            </>
          </div>
        ),
      },
      {
        accessorKey: "Payout",
        header: "Payout",
        size: 50,
        Cell: ({ cell }) => (
          <div className="flex">
            {cell.row.original?.type === "debit" ? (
              <div class="bg-slate-100 max-w-20 w-full text-center text-gray-700 rounded relative" role="alert">
                <strong class="font-bold">-</strong>
              </div>
            ) : (
              <>
                {cell.row.original?.payout} {cell.row.original.currency}{" "}
              </>
            )}
          </div>
        ),
      },
      {
        accessorKey: "#",
        header: "Actions",
        size: 50,
        Cell: ({ cell }) => (
          <div>
            <button
              className="cursor-pointer text-primary/70 hover:text-primary"
              onClick={() => {
                setIsRowDetailModalOpen(true);
                setSelectedRowDetail(cell.row.original);
              }}
            >
              <FaInfoCircle size={20} />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="flex flex-col  w-full trasnfer-history">
      {" "}
      {/* Ensure full height */}
      <header className="rounded-lg sm:text-start text-center bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-bold md:text-left text-center">{t("Casino Bets")}</h1>
      </header>
      <div className="flex-1 pt-4">
        <div className="w-full    rounded-lg ">
          <SelectedDates
            title="Transaction Date"
            setSelectedDate={setSelectedDate}
            selectedDate={selectedDate}
            setCustomDate={setChosenDate}
            setShowReportData={setShowReportData}
          />
          <div className="flex flex-col lg:flex-row space-x-0 space-y-2 lg:space-x-2 lg:space-y-0 w-full">
            <div className="flex flex-row lg:flex-row w-full flex-wrap lg:gap-4">
              <div className="w-[100%] sm:w-[10%] lg:w-[48%] 2xl:w-[31%]">
                <SelectDropDown
                  label="Game"
                  placeholder={"Type 3+ letters"}
                  selectedOption={selectedGameOptions ? selectedGameOptions : []}
                  loadOptions={fetchGames}
                  handleChange={handleGameChange}
                  isSearchable={true}
                />
              </div>
              <div className=" w-[100%] sm:w-[10%] lg:w-[48%] 2xl:w-[31%]">
                <label className="block text-md ml-3  text-[#242424]">{t("Bet Id")}</label>
                <input
                  name="betID"
                  id="betID"
                  autocomplete="false"
                  data-1p-ignore="true"
                  className="text-black w-full rounded-md border px-2 mt-[0.4px] py-1.5"
                  value={betId}
                  onChange={(e) => {
                    setBetId(e.target.value);
                  }}
                />
              </div>
              <RoleBasedDropdowns
                baseRole={user?.role}
                selectedRole={selectedRole}
                setSelectedRole={setSelectedRole}
                selectedUserValues={selectedInputValues}
                setSelectedInputValues={setSelectedInputValues}
              />
            </div>
          </div>
          {/* Action Buttons */}
          <div className="lg:flex gap-3 mb-8 mt-7">
            <button
              onClick={handelSearchData}
              className="bg-primary hover:bg-primary/80 p-2 text-black w-full lg:max-w-40 font-bold lg:py-2 px-11 rounded-lg"
              // className={`bg-primary hover:bg-primary/80 p-2  text-black w-full lg:max-w-40 font-bold lg:py-2 px-11 rounded-lg ${
              //   selectedSuperAgentOption ? " text-black cursor-pointer" : "bg-[#f2c41a] text-black cursor-not-allowed opacity-50"
              // }`}
              type="button"
            >
              {t("SEARCH")}
            </button>
            <button
              className="border border-black hover:bg-gray-300 p-2 mt-4 sm:mt-0  text-gray-700 lg:max-w-40 font-bold w-full lg:py-2 px-11 rounded-lg"
              type="reset"
              onClick={() => {
                setSelectedDate("today");
                setChosenDate("");
                setErrorMessage(null);
                setShowReportData(false);
                setSelectedGameOptions(null);
                setCasinoBetsResponse([]);
                setBetId("");
              }}
            >
              {t("RESET")}
            </button>
          </div>

          <hr className="border-t mt-5 mb-3 border-[#d4c8c8a3]" />

          {/* Transfer History Display */}
          {!casinoBetsLoading && casinoBetsResponse?.length ? (
            <>
              <CustomTable columns={tableColumns} data={tableData ? tableData : []} enableGlobalFilter={true} enableTopToolbar={true} />
            </>
          ) : casinoBetsLoading ? (
            <div className="text-center w-full">
              <SectionLoader bgColor="light" />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 px-3 my-7">
              <div className="bg-[#EDEEF5] rounded-lg p-6 sm:p-8 [&_svg_path]:fill-slate-500">
                <IconSearch />
              </div>
              <span className="text-lg sm:text-xl mt-8 mb-4 text-[#474747] text-center w-full max-w-[300px]">
                {t("Search all available records by selecting one of the filters above!")}
              </span>
            </div>
          )}
        </div>
      </div>
      {isRowDetailModalOpen && selectedRowDetail ? (
        <Modal
          isOpen={isRowDetailModalOpen}
          onClose={() => setIsRowDetailModalOpen(false)}
          className="w-full max-w-[70rem] overflow-y-auto "
          title={
            <>
              {" "}
              <div style={{ display: "flex", alignItems: "center" }} className="px-4 py-4">
                <div className="font-bold text-2xl ">{t("Ticket Details")} </div>
                {selectedRowDetail.type === "debit" ? (
                  <div class="bg-red-500 text-white font-bold text-center rounded max-w-14 ml-3 px-2">{t("Lost")}</div>
                ) : (
                  <div class="bg-green-500 text-white font-bold max-w-14 text-center rounded ml-3 px-2">{t("Won")}</div>
                )}
              </div>
            </>
          }
        >
          <CasinoBetDetailModal data={selectedRowDetail} />
        </Modal>
      ) : null}
    </div>
  );
};

export default CasinoBets;
